import {
  CompressOutlined,
  ExpandOutlined,
  FormOutlined,
  HistoryOutlined,
  LoadingOutlined,
  MinusOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import { Avatar, Button, Drawer, FloatButton, Tooltip, Typography } from "antd";
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";

import { analyticsService, useAppSelector } from "~/store/initializeStore";

import { TrainerAssistantConversation } from "./TrainerAssistantConversation";
import { TrainerAssistantHistoryPalette } from "./TrainerAssistantHistoryPalette";
import { useTrainerAssistantChat, type TrainerAssistantChat } from "./useTrainerAssistantChat";

// Odstęp, o który pływające okienka czatu odsuwają się od prawej krawędzi, żeby nie
// przykryć bubble'a ani okna asystenta (FloatButton 40 px + 24 px marginesu antd).
export const TRAINER_ASSISTANT_BUBBLE_GUTTER = 72;
// Otwarte okno asystenta: 24 px marginesu + 400 px okna + 8 px odstępu. Okienka czatu z klientkami
// muszą wtedy odsunąć się o całą szerokość, inaczej leżą pod oknem asystenta (review TRE-2313).
export const TRAINER_ASSISTANT_WINDOW_GUTTER = 432;
// Okienko rozmowy z klientką: karta 320 px + 20 px odstępu, plus margines od lewej krawędzi.
const CHAT_CARD_SPACE = 340;
const LEFT_EDGE_SPACE = 24;

/** Czy okno asystenta i wszystkie otwarte okienka rozmów mieszczą się obok siebie (TRE-2338). */
export const trainerAssistantWindowFits = (openChats: number, viewportWidth = window.innerWidth) =>
  viewportWidth >= TRAINER_ASSISTANT_WINDOW_GUTTER + openChats * CHAT_CARD_SPACE + LEFT_EDGE_SPACE;

type View = "closed" | "window" | "expanded";

let currentView: View = "closed";
const viewListeners = new Set<() => void>();
const publishView = (view: View) => {
  currentView = view;
  for (const listener of viewListeners) listener();
};
const subscribeView = (listener: () => void) => {
  viewListeners.add(listener);
  return () => viewListeners.delete(listener);
};

// TRE-2338: Dashboard zwija okno asystenta, gdy obok niego musi stanąć coś innego (lista kanałów
// czatu albo nowe okienko rozmowy, które się nie mieści). Rozmowa zostaje, zwija się tylko okno.
const collapseListeners = new Set<() => void>();
export const collapseTrainerAssistantWindow = () => {
  if (currentView !== "window") return;
  for (const listener of collapseListeners) listener();
};

/** Ile miejsca z prawej zostawić okienkom czatu: sam bubble albo całe okno asystenta. */
export const useTrainerAssistantGutter = () => {
  const view = useSyncExternalStore(subscribeView, () => currentView);
  return view === "window" ? TRAINER_ASSISTANT_WINDOW_GUTTER : TRAINER_ASSISTANT_BUBBLE_GUTTER;
};

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
const HISTORY_SHORTCUT = isMac ? "⌘K" : "Ctrl+K";

const Header = ({
  chat,
  view,
  onHistory,
  onToggleExpand,
  onMinimize,
}: {
  chat: TrainerAssistantChat;
  view: Exclude<View, "closed">;
  onHistory: () => void;
  onToggleExpand: () => void;
  onMinimize: () => void;
}) => {
  const title = chat.sessions.find((session) => session.id === chat.activeSessionId)?.title;
  return (
    <div className="flex items-center gap-2">
      <Avatar size={24} className="shrink-0 bg-gray-900" icon={<RobotOutlined />} />
      <div className="flex min-w-0 flex-1 flex-col leading-tight">
        <Typography.Text strong className="text-sm">
          Asystent AI
        </Typography.Text>
        {title ? (
          <Typography.Text type="secondary" ellipsis className="text-xs">
            {title}
          </Typography.Text>
        ) : null}
      </div>
      <Tooltip title={`Historia rozmów (${HISTORY_SHORTCUT})`}>
        <Button type="text" size="small" icon={<HistoryOutlined />} onClick={onHistory} aria-label="Historia rozmów" />
      </Tooltip>
      <Tooltip title="Nowa rozmowa">
        <Button
          type="text"
          size="small"
          icon={<FormOutlined />}
          disabled={!chat.messages.length || chat.busy}
          onClick={chat.startNew}
          aria-label="Nowa rozmowa"
        />
      </Tooltip>
      <Tooltip title={view === "expanded" ? "Zmniejsz do okna" : "Pełny ekran"}>
        <Button
          type="text"
          size="small"
          icon={view === "expanded" ? <CompressOutlined /> : <ExpandOutlined />}
          onClick={onToggleExpand}
          aria-label={view === "expanded" ? "Zmniejsz do okna" : "Pełny ekran"}
        />
      </Tooltip>
      <Tooltip title="Zwiń">
        <Button type="text" size="small" icon={<MinusOutlined />} onClick={onMinimize} aria-label="Zwiń" />
      </Tooltip>
    </div>
  );
};

/**
 * TRE-2313 — asystent AI w panelu. Bubble w prawym dolnym rogu otwiera okno w stylu
 * okienek czatu z klientkami; z okna można przejść do trybu pełnoekranowego (drawer)
 * i z powrotem. Montowany w Dashboard (trwały layout), więc rozmowa, także trwająca
 * odpowiedź, przeżywa nawigację. Bramkę (`useTrainerAssistantEnabled`) sprawdza rodzic.
 */
export const TrainerAssistantHost = () => {
  const [view, setView] = useState<View>("closed");
  const [historyOpen, setHistoryOpen] = useState(false);
  const chat = useTrainerAssistantChat({ visible: view !== "closed" });
  const openChats = useAppSelector((state) => state.chat.activeChannels.length);

  useEffect(() => {
    publishView(view);
    return () => publishView("closed");
  }, [view]);

  useEffect(() => {
    const collapse = () => setView((current) => (current === "window" ? "closed" : current));
    collapseListeners.add(collapse);
    return () => {
      collapseListeners.delete(collapse);
    };
  }, []);

  const open = useCallback(() => {
    // Bez miejsca na okno obok otwartych rozmów asystent otwiera się od razu na pełnym ekranie.
    const fits = trainerAssistantWindowFits(openChats);
    setView(fits ? "window" : "expanded");
    analyticsService.track("trainer_assistant_opened", { source: "bubble", expanded: !fits });
  }, [openChats]);

  const toggleExpand = useCallback(() => {
    setView((current) => {
      const next = current === "expanded" ? "window" : "expanded";
      if (next === "expanded") analyticsService.track("trainer_assistant_expanded");
      return next;
    });
  }, []);

  const openHistory = useCallback(() => {
    setHistoryOpen(true);
    analyticsService.track("trainer_assistant_history_opened");
  }, []);

  useEffect(() => {
    if (view === "closed") return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setHistoryOpen((current) => !current);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [view]);

  const header =
    view === "closed" ? null : (
      <Header
        chat={chat}
        view={view}
        onHistory={openHistory}
        onToggleExpand={toggleExpand}
        onMinimize={() => setView("closed")}
      />
    );

  return (
    <>
      {view === "window" ? (
        <div
          className="fixed bottom-[84px] right-6 flex h-[min(640px,calc(100vh-120px))] w-[400px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl"
          style={{ zIndex: 1001 }}
        >
          <div className="shrink-0 border-b border-gray-100 px-3 py-2">{header}</div>
          <div className="min-h-0 flex-1">
            <TrainerAssistantConversation chat={chat} compact />
          </div>
        </div>
      ) : null}

      <Drawer
        open={view === "expanded"}
        onClose={() => setView("window")}
        width="min(960px, 96vw)"
        closable={false}
        title={view === "expanded" ? header : null}
        styles={{ body: { padding: 0, display: "flex", flexDirection: "column" } }}
      >
        {view === "expanded" ? <TrainerAssistantConversation chat={chat} compact={false} /> : null}
      </Drawer>

      <FloatButton
        type="primary"
        icon={chat.busy ? <LoadingOutlined /> : <RobotOutlined />}
        badge={chat.busy ? { dot: true, color: "gold" } : chat.unread ? { dot: true, color: "green" } : undefined}
        tooltip={view === "closed" ? (chat.unread ? "Nowa odpowiedź asystenta" : "Asystent AI") : undefined}
        style={{ insetInlineEnd: 24, bottom: 24 }}
        onClick={view === "closed" ? open : () => setView("closed")}
      />

      <TrainerAssistantHistoryPalette chat={chat} open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </>
  );
};
